import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  testDbConnection,
  saveDbConnection,
  type SaveDbConnectionParams,
  type DbConnectionInfo,
  type TestDbConnectionResult,
} from '../../services/data-integration-api';

interface ConnectionFormProps {
  connection?: DbConnectionInfo | null;
  onSaved?: (id: string) => void;
  onCancel?: () => void;
}

const DB_TYPE_OPTIONS = [
  { value: 'mysql', label: 'MySQL' },
  { value: 'postgresql', label: 'PostgreSQL' },
];

const CONNECTION_TYPE_OPTIONS = [
  { value: 'source', label: '源库（三方数据来源）' },
  { value: 'target', label: '目标库（同步数据存储）' },
  { value: 'production', label: '生产环境业务库' },
];

const DEFAULT_PORTS: Record<string, number> = {
  mysql: 3306,
  postgresql: 5432,
};

export default function ConnectionForm({ connection, onSaved, onCancel }: ConnectionFormProps) {
  const [form, setForm] = useState({
    connection_name: '',
    connection_type: 'source' as 'source' | 'target' | 'production',
    db_type: 'mysql' as 'mysql' | 'postgresql',
    host: '',
    port: 3306,
    username: '',
    password: '',
    database_name: '',
    ssl_enabled: true,
  });

  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<TestDbConnectionResult | null>(null);
  const [error, setError] = useState('');
  const [showSslWarning, setShowSslWarning] = useState(false);

  useEffect(() => {
    if (connection) {
      setForm({
        connection_name: connection.connection_name,
        connection_type: connection.connection_type as 'source' | 'target' | 'production',
        db_type: connection.db_type as 'mysql' | 'postgresql',
        host: connection.host,
        port: connection.port,
        username: connection.username || '',
        password: '',
        database_name: connection.database_name,
        ssl_enabled: connection.ssl_enabled,
      });
    }
  }, [connection]);

  const updateField = (field: string, value: any) => {
    setForm(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'db_type') {
        next.port = DEFAULT_PORTS[value] || prev.port;
      }
      return next;
    });
    setTestResult(null);
    setError('');
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError('');
    try {
      const result = await testDbConnection({
        host: form.host,
        port: form.port,
        username: form.username,
        password: form.password,
        database_name: form.database_name,
        db_type: form.db_type,
        ssl_enabled: form.ssl_enabled,
      });
      setTestResult(result);
    } catch (e: any) {
      setError(e.message || '测试失败');
    } finally {
      setTesting(false);
    }
  };

  const handleSslToggle = (checked: boolean) => {
    if (!checked) {
      setShowSslWarning(true);
    } else {
      updateField('ssl_enabled', true);
    }
  };

  const confirmSslOff = () => {
    updateField('ssl_enabled', false);
    setShowSslWarning(false);
  };

  const handleSave = async () => {
    if (!form.connection_name || !form.host || !form.username || !form.password || !form.database_name) {
      setError('请填写所有必填字段');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const params: SaveDbConnectionParams = {
        connectionId: connection?._id || null,
        ...form,
      };
      const result = await saveDbConnection(params);
      onSaved?.(result._id);
    } catch (e: any) {
      setError(e.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h3 className="text-lg font-semibold mb-4">{connection ? '编辑连接' : '新建连接'}</h3>

      <div className="space-y-1.5 mb-4">
        <Label>连接名称 *</Label>
        <Input
          type="text"
          value={form.connection_name}
          onChange={e => updateField('connection_name', e.target.value)}
          placeholder="如: ERP生产库"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-1.5">
          <Label>连接用途 *</Label>
          <select
            value={form.connection_type}
            onChange={e => updateField('connection_type', e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {CONNECTION_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <Label>数据库类型 *</Label>
          <select
            value={form.db_type}
            onChange={e => updateField('db_type', e.target.value)}
            className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            {DB_TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-[3fr_1fr] gap-4 mb-4">
        <div className="space-y-1.5">
          <Label>主机地址 *</Label>
          <Input
            type="text"
            value={form.host}
            onChange={e => updateField('host', e.target.value)}
            placeholder="IP 或域名"
          />
        </div>
        <div className="space-y-1.5">
          <Label>端口 *</Label>
          <Input
            type="number"
            value={form.port}
            onChange={e => updateField('port', Number(e.target.value))}
            min={1}
            max={65535}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="space-y-1.5">
          <Label>用户名 *</Label>
          <Input
            type="text"
            value={form.username}
            onChange={e => updateField('username', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label>密码 *</Label>
          <Input
            type="password"
            value={form.password}
            onChange={e => updateField('password', e.target.value)}
            placeholder={connection ? '请重新输入密码' : ''}
          />
          {connection && !form.password && (
            <p className="text-xs text-muted-foreground">编辑时需重新输入密码才能测试连接或保存</p>
          )}
        </div>
      </div>

      <div className="space-y-1.5 mb-4">
        <Label>数据库名 *</Label>
        <Input
          type="text"
          value={form.database_name}
          onChange={e => updateField('database_name', e.target.value)}
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Checkbox
          id="ssl-enabled"
          checked={form.ssl_enabled}
          onCheckedChange={(checked) => handleSslToggle(checked === true)}
        />
        <Label htmlFor="ssl-enabled" className="cursor-pointer">启用 SSL/TLS 加密连接</Label>
      </div>

      {showSslWarning && (
        <div className="bg-[var(--color-warning-light)] border border-[var(--color-warning)] rounded-md p-3 mb-4">
          <p className="text-sm mb-2"><strong>安全警告：</strong>关闭 SSL 将使数据库连接以明文传输，存在安全风险。</p>
          <div className="flex gap-2">
            <Button variant="destructive" size="sm" onClick={confirmSslOff}>确认关闭 SSL</Button>
            <Button variant="ghost" size="sm" onClick={() => setShowSslWarning(false)}>取消</Button>
          </div>
        </div>
      )}

      {error && <p className="text-destructive text-sm mb-3">{error}</p>}

      {testResult && (
        <div className={`rounded-md p-3 mb-4 ${testResult.connected ? 'bg-[var(--color-success-light)]' : 'bg-destructive/10 border border-destructive/30'}`}>
          {testResult.connected ? (
            <span className="text-sm">连接成功 | 版本: {testResult.server_version} | 响应: {testResult.response_time_ms}ms</span>
          ) : (
            <span className="text-sm text-destructive">连接失败: {testResult.error_message}</span>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-4">
        <Button variant="outline" onClick={handleTest} disabled={testing}>
          {testing ? '测试中...' : '测试连接'}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? '保存中...' : '保存'}
        </Button>
        {onCancel && <Button variant="ghost" onClick={onCancel}>取消</Button>}
      </div>
    </div>
  );
}
